import {
	Badge,
	Box,
	Button,
	CircularProgress,
	Divider,
	Icon,
	IconButton,
	List,
	ListItemButton,
	Popover
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { type NotificationFragment } from "~/gql/queries/notification.generated";
import {
	isNotificationUnread,
	notificationField,
	notificationIcon,
	notificationRoute,
	notificationSecondaryLine,
	notificationTranslationKey
} from "~/helpers/notificationPresentation";
import useNotificationMutations from "~/hooks/mutations/useNotificationMutations";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useRelativeTimestamp from "~/hooks/useRelativeTimestamp";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import { useGetMyNotificationsQuery, useGetMyUnreadNotificationCountQuery } from "~/services/endpoints/apiEndpoints";

const RECENT_NOTIFICATION_COUNT = 5;
const MAX_BADGE_COUNT = 99;
const STALLED_SOCKET_FALLBACK_POLL_MS = 60_000;
const ALL_NOTIFICATIONS_ROUTE = "/notifications";

const NotificationBell = () => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const formatRelativeTimestamp = useRelativeTimestamp();
	const { isEnabled } = useFeatureFlag(FeatureFlagName.Notifications);
	const { isEnabled: isScheduledTransfersEnabled } = useFeatureFlag(FeatureFlagName.ScheduledItemTransfers);
	const { isEnabled: isLearningPathEnabled } = useFeatureFlag(FeatureFlagName.LearningPath);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const { data: unreadCountData, isError: isUnreadCountError } = useGetMyUnreadNotificationCountQuery(undefined, {
		pollingInterval: STALLED_SOCKET_FALLBACK_POLL_MS,
		skip: !isEnabled
	});
	// `order` omitted so the server's default createdAt-desc-then-id-desc ordering applies.
	const {
		data: notificationsData,
		isError: isNotificationsError,
		isFetching,
		refetch: refetchNotifications
	} = useGetMyNotificationsQuery({ first: RECENT_NOTIFICATION_COUNT, unreadOnly: false }, { skip: !isEnabled });
	const { markAllNotificationsRead, markNotificationRead } = useNotificationMutations();

	if (!isEnabled) return null;

	const open = Boolean(anchorEl);
	const notifications = notificationsData?.myNotifications?.nodes ?? [];
	// A failed count must not masquerade as zero, so the badge hides and the loaded rows decide instead.
	const badgeCount = isUnreadCountError ? 0 : (unreadCountData?.myUnreadNotificationCount ?? 0);
	const hasUnread = isUnreadCountError ? notifications.some(isNotificationUnread) : badgeCount > 0;

	const handleRowClick = (notification: NotificationFragment) => {
		setAnchorEl(null);
		const route = notificationRoute(notification, { isLearningPathEnabled, isScheduledTransfersEnabled });
		const openNotification = () => {
			if (route) navigate(route);
			if (isNotificationUnread(notification)) void markNotificationRead(notification.id);
		};
		if (route) runGuardedExit(openNotification);
		else openNotification();
	};

	const handleSeeAllClick = () => {
		setAnchorEl(null);
		runGuardedExit(() => navigate(ALL_NOTIFICATIONS_ROUTE));
	};

	return (
		<>
			<IconButton
				aria-expanded={open}
				aria-haspopup="menu"
				aria-label={
					badgeCount > 0
						? t("notifications.bellLabelWithUnread", { unreadCount: badgeCount })
						: t("notifications.bellLabel")
				}
				color="inherit"
				onClick={(event) => setAnchorEl(event.currentTarget)}
				size="small"
			>
				<Badge badgeContent={badgeCount} color="error" max={MAX_BADGE_COUNT}>
					<Icon>notifications</Icon>
				</Badge>
			</IconButton>

			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				onClose={() => setAnchorEl(null)}
				open={open}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box sx={{ maxWidth: 380, minWidth: 320 }}>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							gap: 1,
							justifyContent: "space-between",
							px: 1.5,
							py: 1
						}}
					>
						<BodyTextSmall bold>{t("notifications.title")}</BodyTextSmall>
						<Button
							color="primary"
							disabled={!hasUnread}
							onClick={() => markAllNotificationsRead()}
							size="small"
							sx={{ textTransform: "none" }}
						>
							{t("notifications.markAllAsRead")}
						</Button>
					</Box>
					<Divider />

					{isFetching && notifications.length === 0 ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : isNotificationsError ? (
						<RetryableErrorState dense message={t("notifications.loadError")} onRetry={() => refetchNotifications()} />
					) : notifications.length === 0 ? (
						<Box sx={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 1, px: 2, py: 3 }}>
							<Icon sx={{ color: "text.disabled" }}>notifications_off</Icon>
							<CaptionText color="text.secondary">{t("notifications.empty")}</CaptionText>
						</Box>
					) : (
						<List dense disablePadding>
							{notifications.map((notification) => {
								const unread = isNotificationUnread(notification);
								const kindKey = notificationTranslationKey(notification.kind);
								const body = notificationSecondaryLine(notification, t);
								return (
									<ListItemButton
										key={notification.id}
										onClick={() => handleRowClick(notification)}
										sx={{ alignItems: "flex-start", gap: 1.25, px: 1.5, py: 1 }}
									>
										<Icon sx={{ color: "text.secondary", fontSize: "1.25rem", mt: 0.25 }}>
											{notificationIcon(notification.kind)}
										</Icon>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<BodyTextSmall bold={unread}>
												{t(`notifications.kinds.${kindKey}`, {
													fileName: notificationField(notification, "fileName"),
													learnerName: notificationField(notification, "learnerName"),
													orderNumber: notificationField(notification, "orderNumber"),
													reportName: notificationField(notification, "reportName")
												})}
											</BodyTextSmall>
											{body && (
												<CaptionText color="text.secondary" noWrap sx={{ display: "block" }}>
													{body}
												</CaptionText>
											)}
											<CaptionText color="text.disabled" sx={{ display: "block" }}>
												{formatRelativeTimestamp(notification.createdAt)}
											</CaptionText>
										</Box>
										{unread && (
											<Box
												sx={{
													bgcolor: "error.main",
													borderRadius: "50%",
													flexShrink: 0,
													height: 8,
													mt: 0.75,
													width: 8
												}}
											/>
										)}
									</ListItemButton>
								);
							})}
						</List>
					)}

					<Divider />
					<Button fullWidth onClick={handleSeeAllClick} size="small" sx={{ py: 1, textTransform: "none" }}>
						{t("notifications.seeAll")}
					</Button>
				</Box>
			</Popover>
		</>
	);
};

export default NotificationBell;
